<template>
    <CompassTable :isCheckable="false" class="rate-category-table" size="medium">
        <CompassTableHead @sort="handleSort">
            <CompassTableHeadCell>
                {{ $t.__('settings.taxes.VatSettings.rates.table.header.by_default') }}
            </CompassTableHeadCell>
            <CompassTableHeadCell
                :sortable="{
                    direction: '',
                    orderBy: 'category',
                }"
            >
                {{ $t.__('settings.taxes.VatSettings.rates.table.header.category') }}
            </CompassTableHeadCell>
            <CompassTableHeadCell
                :sortable="{
                    direction: '',
                    orderBy: 'documents',
                }"
            >
                {{ $t.__('settings.taxes.VatSettings.rates.table.header.documents') }}
            </CompassTableHeadCell>
            <CompassTableHeadCell
                :sortable="{
                    direction: '',
                    orderBy: 'applied_vat',
                }"
            >
                {{ $t.__('settings.taxes.VatSettings.rates.table.header.applied_vat') }}
            </CompassTableHeadCell>
            <CompassTableHeadCell>
                {{ $t.__('settings.taxes.VatSettings.rates.table.header.document_currency') }}
            </CompassTableHeadCell>
            <CompassTableHeadCell
                :sortable="{
                    direction: '',
                    orderBy: 'accounting_code',
                }"
            >
                {{ $t.__('settings.taxes.VatSettings.rates.table.header.accounting_code') }}
            </CompassTableHeadCell>
            <CompassTableHeadCell />
            <CompassTableHeadCell />
        </CompassTableHead>
        <CompassTableBody>
            <CompassTableRow
                v-for="rate in localRateCategories"
                :key="rate.id"
                class="rate-category-table__row"
            >
                <CompassTableBodyCell
                    alignContent="center"
                    class="rate-category-table__fixed-width-cell"
                >
                    <CompassRadio
                        :modelValue="rate.id"
                        label=""
                        :checked="rate.id === defaultRateCategory"
                        @change="handleDefaultRateChange(rate)"
                    />
                </CompassTableBodyCell>
                <CompassTableBodyCell class="rate-category-table__label-cell">
                    <div>
                        {{ rate.label }}
                        <div class="rate-category-table__caption">
                            {{ getRatePriceTypeLabel(rate) }}
                        </div>
                    </div>
                    <CompassTooltip
                        v-if="showConformityTag && !rate.is_einvoicing_compliant"
                        :content="$t.__('settings.taxes.VatSettings.rates.conformity_tooltip')"
                        :arrow="false"
                        class="conformity-tag-tooltip"
                    >
                        <CompassTag size="small" color="yellow" icon="tool">
                            {{ $t.__('listing.FacturXConformityColumn.non_conform') }}
                        </CompassTag>
                    </CompassTooltip>
                </CompassTableBodyCell>
                <CompassTableBodyCell>
                    <template v-if="rate.includes_taxes">
                        {{ $t.__('common.labels.taxes_included') }}
                    </template>
                    <template v-else>
                        {{ $t.__('common.labels.taxes_excluded') }}
                    </template>
                </CompassTableBodyCell>
                <CompassTableBodyCell> {{ getVatLabel(rate) }} </CompassTableBodyCell>
                <CompassTableBodyCell class="rate-category-table__multi-line-cell">
                    {{ rate._embed.currency.symbol }}
                    <div
                        v-if="isDefaultCurrency(rate)"
                        class="rate-category-table__caption typographies-paragraph-small-regular"
                    >
                        {{ $t.__('settings.taxes.VatSettings.rates.table.account_currency') }}
                    </div>
                </CompassTableBodyCell>
                <CompassTableBodyCell>
                    {{
                        rate.accounting.accounting_code_id === null
                            ? $t.__(
                                  'settings.taxes.VatSettings.rates.table.accounting_code_product',
                              )
                            : getAccountingCodeById(rate.accounting.accounting_code_id)
                    }}
                </CompassTableBodyCell>
                <CompassTableBodyCell>
                    <CompassTooltip :content="deleteButtonTooltip(rate)" :arrow="false">
                        <CompassIconButton
                            :disabled="rate.is_default"
                            icon="bin"
                            variant="destructive"
                            @click="deleteRateCategory(rate.id)"
                        />
                    </CompassTooltip>
                </CompassTableBodyCell>
                <CompassTableBodyCell>
                    <CompassTooltip :content="$t.__('common.actions.edit_alt')" :arrow="false">
                        <CompassIconButton icon="edit" @click="handleEdit(rate)" />
                    </CompassTooltip>
                </CompassTableBodyCell>
            </CompassTableRow>
        </CompassTableBody>
    </CompassTable>
</template>
<script>
import Feature from 'Services/Feature';
import { createNamespacedHelpers } from 'vuex';
const { mapGetters: vatMapGetters } = createNamespacedHelpers('vatStore');

export default {
    props: {
        rateCategories: {
            type: Array,
            required: true,
        },
        metadata: {
            type: Object,
            required: true,
        },
        taxes: {
            type: Array,
            required: true,
        },
    },
    emits: ['delete-rate-category', 'edit-rate-category', 'change-default-rate'],
    data() {
        return {
            localRateCategories: this.rateCategories,
        };
    },
    computed: {
        ...vatMapGetters(['defaultRateCategory']),
        showConformityTag() {
            return Feature.isEnabled('einvoicing-vat-parameters');
        },
    },
    watch: {
        rateCategories: {
            handler(newVal) {
                this.localRateCategories = newVal;
            },
            deep: true,
        },
    },
    methods: {
        deleteButtonTooltip(item) {
            if (item.is_default) {
                return this.$t.__('settings.taxes.VatSettings.rates.table.delete_button.tooltip');
            }
            return this.$t.__('common.actions.delete');
        },
        getVatLabel(rateCategory) {
            const tax = this.taxes.find((item) => item.id === rateCategory.tax_id);
            return tax
                ? `${parseFloat(tax.rate)} %`
                : this.$t.__('settings.taxes.VatSettings.rates.table.product_tax');
        },
        getRatePriceTypeLabel(rateCategory) {
            return rateCategory.price.type === 'value'
                ? this.$t.__('settings.taxes.VatSettings.rates.table.price.value')
                : `${rateCategory.price.value} ${this.$t.__(
                      'settings.taxes.VatSettings.rates.table.price.percent',
                  )}`;
        },
        getAccountingCodeById(id) {
            const code = this.metadata.accounting.accounting_codes.find(
                (item) => item.value === id,
            );
            return code?.code ?? '';
        },
        isDefaultCurrency(rateCategory) {
            return rateCategory._embed?.currency.is_default;
        },
        handleSort({ orderBy, direction }) {
            this.localRateCategories.sort((a, b) => {
                let aValue = a[orderBy];
                let bValue = b[orderBy];

                // Handle nested properties if needed
                if (orderBy === 'applied_vat') {
                    aValue = this.getVatLabel(a);
                    bValue = this.getVatLabel(b);
                } else if (orderBy === 'category') {
                    aValue = a.label;
                    bValue = b.label;
                } else if (orderBy === 'documents') {
                    aValue = a.includes_taxes ? 1 : 0;
                    bValue = b.includes_taxes ? 1 : 0;
                } else if (orderBy === 'accounting_code') {
                    aValue = this.getAccountingCodeById(a.accounting.accounting_code_id);
                    bValue = this.getAccountingCodeById(b.accounting.accounting_code_id);
                }

                if (aValue < bValue) return direction === 'asc' ? -1 : 1;
                if (aValue > bValue) return direction === 'asc' ? 1 : -1;
                return 0;
            });
        },
        deleteRateCategory(id) {
            this.$emit('delete-rate-category', id);
        },
        handleEdit(category) {
            this.$emit('edit-rate-category', category);
        },
        handleDefaultRateChange(rate) {
            this.$emit('change-default-rate', rate);
        },
    },
};
</script>
<style lang="scss" scoped>
.rate-category-table {
    :deep() {
        .compass-radio__text-container {
            display: none;
        }
    }

    &__fixed-width-cell {
        white-space: nowrap;
        width: 1%;
    }

    &__multi-line-cell {
        :deep() {
            .compass-table__cell-container {
                align-items: start;
                flex-direction: column;
            }
        }
    }

    &__caption {
        color: var(--colors-common-text-standard-caption);
    }

    &__label-cell {
        &:deep(.compass-table__cell-container) {
            gap: var(--spacing-1);
        }

        .conformity-tag-tooltip {
            flex-shrink: 0;
        }
    }
}
</style>
